const { heicTo } = require("heic-to/csp");

const FilePreviewMediaPort = {
  add: function (app) {
    const layoutObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const requestId = entry.target.getAttribute("data-fp-request-id");
        if (!requestId || !app.ports.previewLayoutSized) {
          continue;
        }
        const cr = entry.contentRect;
        app.ports.previewLayoutSized.send({
          requestId: requestId,
          width: cr.width,
          height: cr.height,
        });
      }
    });

    const observedLayoutElements = new Map();

    if (app.ports.convertHeicFromUrl) {
      app.ports.convertHeicFromUrl.subscribe(async ({ requestId, url }) => {
        try {
          const res = await fetch(url);
          if (!res.ok) {
            throw new Error("fetch failed");
          }
          const blob = await res.blob();
          const outBlob = await heicTo({
            blob,
            type: "image/jpeg",
            quality: 0.9,
          });
          const objectUrl = URL.createObjectURL(outBlob);
          app.ports.heicPreviewResult.send({ requestId: requestId, url: objectUrl });
        } catch {
          app.ports.heicPreviewResult.send({ requestId: requestId, url: null });
        }
      });
    }

    if (app.ports.observePreviewLayout) {
      app.ports.observePreviewLayout.subscribe(({ elementId, requestId }) => {
        const el = document.getElementById(elementId);
        if (!el) {
          return;
        }
        const prev = observedLayoutElements.get(elementId);
        if (prev) {
          layoutObserver.unobserve(prev);
        }
        el.setAttribute("data-fp-request-id", requestId);
        layoutObserver.observe(el);
        observedLayoutElements.set(elementId, el);
        const r = el.getBoundingClientRect();
        if (app.ports.previewLayoutSized) {
          app.ports.previewLayoutSized.send({
            requestId: requestId,
            width: r.width,
            height: r.height,
          });
        }
      });
    }

    if (app.ports.revokePreviewObjectUrl) {
      app.ports.revokePreviewObjectUrl.subscribe((u) => {
        try {
          if (u && String(u).startsWith("blob:")) {
            URL.revokeObjectURL(u);
          }
        } catch {
          void 0;
        }
      });
    }

    if (app.ports.mediaPlay) {
      app.ports.mediaPlay.subscribe(({ elementId }) => {
        const el = document.getElementById(elementId);
        if (el && (el.tagName === "AUDIO" || el.tagName === "VIDEO")) {
          void el.play().catch(() => {});
        }
      });
    }

    if (app.ports.mediaTogglePlayPause) {
      app.ports.mediaTogglePlayPause.subscribe(({ elementId }) => {
        const el = document.getElementById(elementId);
        if (!el || (el.tagName !== "AUDIO" && el.tagName !== "VIDEO")) {
          return;
        }
        if (el.paused) {
          void el.play().catch(() => {});
        } else {
          el.pause();
        }
      });
    }

    if (app.ports.mediaSetCurrentTime) {
      app.ports.mediaSetCurrentTime.subscribe(({ elementId, seconds }) => {
        const el = document.getElementById(elementId);
        if (!el || el.tagName !== "AUDIO") {
          return;
        }
        const t = Number.isFinite(seconds) ? seconds : 0;
        el.currentTime = Math.max(0, t);
      });
    }

    if (app.ports.videoSeekPreview) {
      app.ports.videoSeekPreview.subscribe(({ videoElementId }) => {
        const v = document.getElementById(videoElementId);
        if (!v || v.tagName !== "VIDEO") {
          return;
        }
        const run = () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const d = v.duration;
              if (d && !Number.isNaN(d) && d > 0) {
                const t = Math.min(0.5, d * 0.1);
                v.currentTime = t > 0 ? t : 0;
              }
            });
          });
        };
        if (v.readyState >= 1 && Number.isFinite(v.duration) && v.duration > 0) {
          run();
        } else {
          v.addEventListener("loadedmetadata", run, { once: true });
        }
      });
    }

    return function cleanup() {
      observedLayoutElements.forEach((element) => {
        layoutObserver.unobserve(element);
      });
      observedLayoutElements.clear();
      layoutObserver.disconnect();
    };
  },
};

module.exports = FilePreviewMediaPort;
